/** XeAgents.ai**/
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuthDialog } from "@/components/landing/AuthDialog";
import { AuthStorage } from "@/lib/auth-storage";
import { FREE_VOICE_CREDITS } from "@/lib/free-credits";
import { useFreeCreditTerms } from "@/components/landing/useFreeCreditTerms";

/**
 * The offer restated mid-page, for the reader who scrolled past the hero card
 * while working out what the product does.
 */
export function FreeCreditsBanner() {
  const { t } = useTranslation("landing");
  const freeCreditTerms = useFreeCreditTerms();
  const { openAuth } = useAuthDialog();
  const shouldReduceMotion = useReducedMotion();
  const isAuthenticated = AuthStorage.isAuthenticated();
  const isSuperAdmin = AuthStorage.isSuperAdmin();

  const handleClaim = () => {
    if (isAuthenticated) {
      window.location.href = isSuperAdmin ? "/admin" : "/app";
      return;
    }
    openAuth("register");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="free-credits-banner">
      <motion.div
        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-950 via-purple-950 to-slate-950 px-6 py-10 text-center sm:px-10 sm:py-14"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_0%,rgba(139,92,246,0.35),transparent)]" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

        <div className="relative mx-auto flex max-w-3xl flex-col items-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20">
            <Gift className="h-7 w-7 text-amber-300" />
          </span>

          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-violet-300">
            {t("freeCredits.eyebrow")}
          </p>

          <h2 className="mt-3 text-2xl font-bold text-white sm:text-3xl md:text-4xl">
            {t("freeCredits.title", { count: FREE_VOICE_CREDITS })}
          </h2>

          <p className="mt-3 text-sm text-slate-300 sm:text-base">
            {t("freeCredits.body")}
          </p>

          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {freeCreditTerms.map((term) => (
              <li key={term} className="flex items-center gap-2 text-sm text-slate-200">
                <Check className="h-4 w-4 flex-shrink-0 text-emerald-400" />
                {term}
              </li>
            ))}
          </ul>

          <Button
            size="lg"
            onClick={handleClaim}
            className="group mt-8 h-14 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-8 text-base font-semibold text-white shadow-xl shadow-violet-900/50 hover:from-violet-500 hover:to-purple-500"
            data-testid="button-banner-free-credits"
          >
            {t("credits.get", { count: FREE_VOICE_CREDITS })}
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default FreeCreditsBanner;
